import {
  DevicesDeviceCommand,
  useExecuteCommand,
  type DevicesDevice,
  type DevicesDeviceState,
} from '@smarthome/api';
import { useDebounce } from '@smarthome/ui';
import { AlertCircle, ChevronDown, ChevronUp, Minus, Plus } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getDeviceIdForApi, getDeviceOn } from '../../utils';

import { SearchField, Select, Slider, Toggle } from '@/components/ui';

export interface TVControlsProps {
  device: DevicesDevice;
  onUpdateState: (state: DevicesDeviceState) => void;
  disabled?: boolean;
}

export function TVControls({ device, onUpdateState, disabled }: TVControlsProps) {
  const capabilities = device.capabilities?.tv;
  const tvState = device.state?.tv;
  const isOn = getDeviceOn(device);
  const deviceId = getDeviceIdForApi(device);

  // Command error state
  const [commandError, setCommandError] = useState<string | null>(null);

  // Local state for volume slider
  const rawVolume = tvState?.volume ?? 0;
  const [localVolume, setLocalVolume] = useState(rawVolume);
  const debouncedVolume = useDebounce(localVolume, 300);

  // Channel search
  const [channelSearch, setChannelSearch] = useState('');

  // Track if we initiated the change
  const isUserChange = useRef(false);
  const lastSentVolume = useRef(debouncedVolume);

  // Sync local state with device state
  useEffect(() => {
    if (!isUserChange.current) {
      setLocalVolume(rawVolume);
    }
  }, [rawVolume]);

  // Send debounced volume updates
  useEffect(() => {
    if (isUserChange.current && debouncedVolume !== lastSentVolume.current) {
      lastSentVolume.current = debouncedVolume;
      onUpdateState({ tv: { volume: debouncedVolume } });
      isUserChange.current = false;
    }
  }, [debouncedVolume, onUpdateState]);

  // Execute command hook
  const { mutate: executeCommand } = useExecuteCommand({
    mutation: {
      onError: () => {
        setCommandError('Command failed. This device may not support this action.');
        setTimeout(() => setCommandError(null), 3000);
      },
    },
  });

  const handleCommand = useCallback(
    (command: DevicesDeviceCommand) => {
      if (!deviceId) return;
      setCommandError(null);
      executeCommand({ id: deviceId, data: { command } });
    },
    [deviceId, executeCommand]
  );

  const handlePowerToggle = (checked: boolean) => {
    onUpdateState({ tv: { on: checked } });
  };

  const handleVolumeChange = (value: number) => {
    isUserChange.current = true;
    setLocalVolume(value);
  };

  const handleMuteToggle = (checked: boolean) => {
    onUpdateState({ tv: { muted: checked } });
  };

  const handleInputSourceChange = (sourceId: string) => {
    onUpdateState({ tv: { source: sourceId } });
  };

  const handleChannelSelect = (channelId: string) => {
    onUpdateState({ tv: { source: channelId } });
  };

  // Input source options - filter out sources without an ID
  const inputSourceOptions = useMemo(
    () =>
      (capabilities?.inputSources ?? [])
        .filter((src) => src.id)
        .map((src) => ({
          value: src.id!,
          label: src.label ?? src.id ?? 'Unknown',
        })),
    [capabilities?.inputSources]
  );

  // Filter channels by search and exclude those without an ID
  const filteredChannels = useMemo(() => {
    const channels = (capabilities?.channels ?? []).filter((ch) => ch.id);
    if (!channelSearch.trim()) return channels;

    const query = channelSearch.toLowerCase().trim();
    return channels.filter((ch) => {
      const name = (ch.name ?? '').toLowerCase();
      const number = ch.number?.toString() ?? '';
      return name.includes(query) || number.includes(query);
    });
  }, [capabilities?.channels, channelSearch]);

  // Check if volume controls should be shown
  // Volume slider is always shown when TV volume state exists
  const showVolumeSlider = tvState?.volume !== undefined;
  // Volume increment buttons (+/-) are only shown when the capability is supported
  const showVolumeIncrement = capabilities?.volumeIncrement === true;
  const showMuteControl = capabilities?.mute === true;
  const showInputSourceSelect =
    capabilities?.inputSourceSelect === true && (capabilities?.inputSources?.length ?? 0) > 0;
  const showChannelSelector =
    (capabilities?.channels?.length ?? 0) > 0 &&
    (capabilities?.channelByName === true || capabilities?.channelByNumber === true);
  const showChannelIncrement = capabilities?.channelIncrement === true;

  // Check if we have any channels section
  const hasChannelsSection = showChannelSelector || showChannelIncrement;

  return (
    <div className="-m-4 space-y-5 p-4">
      {/* Command error */}
      {commandError && (
        <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{commandError}</span>
        </div>
      )}

      {/* Two-column grid layout on desktop */}
      <div className={`grid gap-6 ${hasChannelsSection ? 'md:grid-cols-2' : ''}`}>
        {/* Left Column: Remote Section */}
        <div className="space-y-3">
          <h3 className="text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
            Remote
          </h3>

          {/* Power and Mute Card */}
          <div className="rounded-xl bg-white p-1 ring-1 ring-gray-200 dark:bg-zinc-900 dark:ring-white/[0.06]">
            {/* Power toggle row */}
            <div className="rounded-lg px-3 py-2.5 transition-colors hover:bg-gray-50 dark:hover:bg-white/[0.02]">
              <Toggle
                label="Power"
                layout="switch-right"
                checked={isOn ?? false}
                onChange={handlePowerToggle}
                disabled={disabled}
              />
            </div>

            {/* Mute toggle row (only if supported) */}
            {showMuteControl && (
              <>
                <div className="mx-3 h-px bg-gray-200 dark:bg-white/[0.04]" />
                <div className="rounded-lg px-3 py-2.5 transition-colors hover:bg-gray-50 dark:hover:bg-white/[0.02]">
                  <Toggle
                    label="Mute"
                    layout="switch-right"
                    checked={tvState?.muted ?? false}
                    onChange={handleMuteToggle}
                    disabled={disabled}
                  />
                </div>
              </>
            )}
          </div>

          {/* Volume Card - Integrated slider with +/- buttons */}
          {showVolumeSlider && (
            <div className="rounded-xl bg-white p-4 ring-1 ring-gray-200 dark:bg-zinc-900 dark:ring-white/[0.06]">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-zinc-300">Volume</span>
                <span className="text-sm text-gray-500 dark:text-zinc-500">{localVolume}%</span>
              </div>
              {/* Integrated slider with optional +/- buttons */}
              <div className="flex items-center gap-3">
                {showVolumeIncrement && (
                  <button
                    type="button"
                    onClick={() => handleCommand(DevicesDeviceCommand.DeviceCommandVolumeDown)}
                    disabled={disabled}
                    className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
                    aria-label="Volume down"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                )}
                <Slider
                  label="Volume"
                  hideLabel
                  min={0}
                  max={100}
                  value={localVolume}
                  onChange={handleVolumeChange}
                  showValue={false}
                  disabled={disabled}
                  className="flex-1"
                />
                {showVolumeIncrement && (
                  <button
                    type="button"
                    onClick={() => handleCommand(DevicesDeviceCommand.DeviceCommandVolumeUp)}
                    disabled={disabled}
                    className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
                    aria-label="Volume up"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Input Source Card */}
          {showInputSourceSelect && (
            <div className="rounded-xl bg-white p-4 ring-1 ring-gray-200 dark:bg-zinc-900 dark:ring-white/[0.06]">
              <Select
                label="Input Source"
                value={tvState?.source ?? ''}
                onChange={handleInputSourceChange}
                options={inputSourceOptions}
                placeholder="Select input..."
                disabled={disabled}
                fullWidth
              />
            </div>
          )}
        </div>

        {/* Right Column: Channels Section */}
        {hasChannelsSection && (
          <div className="space-y-3">
            <h3 className="text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
              Channels
            </h3>

            {/* Channel Navigation Pad */}
            {showChannelIncrement && (
              <div className="rounded-xl bg-white p-4 ring-1 ring-gray-200 dark:bg-zinc-900 dark:ring-white/[0.06]">
                <div className="flex items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={() => handleCommand(DevicesDeviceCommand.DeviceCommandChannelDown)}
                    disabled={disabled}
                    className="flex h-12 w-12 items-center justify-center rounded-xl bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
                    aria-label="Channel down"
                  >
                    <ChevronDown className="h-5 w-5" />
                  </button>
                  <div className="text-center">
                    <p className="text-xs text-gray-500 dark:text-zinc-500">Navigation</p>
                    <p className="text-sm font-medium text-gray-700 dark:text-zinc-300">Channels</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleCommand(DevicesDeviceCommand.DeviceCommandChannelUp)}
                    disabled={disabled}
                    className="flex h-12 w-12 items-center justify-center rounded-xl bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
                    aria-label="Channel up"
                  >
                    <ChevronUp className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}

            {/* Channel Search and List */}
            {showChannelSelector && (
              <div className="rounded-xl bg-white p-4 ring-1 ring-gray-200 dark:bg-zinc-900 dark:ring-white/[0.06]">
                <SearchField
                  label="Search channels"
                  hideLabel
                  placeholder="Search channels..."
                  value={channelSearch}
                  onChange={(e) => setChannelSearch(e.target.value)}
                  disabled={disabled}
                />
                <div className="mt-3 max-h-48 space-y-0.5 overflow-y-auto rounded-lg">
                  {filteredChannels.length === 0 ? (
                    <div className="px-3 py-4 text-center text-sm text-gray-500 dark:text-zinc-500">
                      No channels found
                    </div>
                  ) : (
                    filteredChannels.map((channel) => {
                      const isSelected = tvState?.source === channel.id;

                      return (
                        <button
                          key={channel.id}
                          type="button"
                          onClick={() => handleChannelSelect(channel.id!)}
                          disabled={disabled}
                          className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm transition-colors ${
                            isSelected
                              ? 'bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400'
                              : 'text-gray-700 hover:bg-gray-50 dark:text-zinc-300 dark:hover:bg-white/[0.02]'
                          } ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                        >
                          <span>
                            {channel.number !== undefined && (
                              <span className="mr-2 font-mono text-xs text-gray-400 dark:text-zinc-500">
                                {channel.number}
                              </span>
                            )}
                            {channel.name ?? 'Unknown'}
                          </span>
                          {isSelected && (
                            <span className="rounded bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-500/20 dark:text-blue-400">
                              Current
                            </span>
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
